import React from "react";
import styled from "styled-components";
import { corners, gap, marginBottom, marginRight, marginY, padding, typography, dateFormats, timeFormats } from "../../styles";
import { getTZAbbreviation } from "../../../utils";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
export type BookingSummaryCardProps = {
  serviceName: string;
  organisationName: string;
  resourceName: string | null;
  startTime: number;
  endTime: number;
  timezone: string;
  locale: string | null;
};
const Container = styled.div`
  width: 100%;
  ${marginBottom.m}
  border: 1px solid ${({
  theme
}) => theme.brand.cssVars["--d-border"]};
  border-top: 4px solid ${({
  theme
}) => theme.brand.cssVars["--d-accent"]};
  ${corners.m}
  ${padding.m}
  background: ${({
  theme
}) => theme.brand.cssVars["--d-card"]};
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${gap.s}
  ${marginBottom.xs}
  text-align: left;
`;
const Text = styled.span`
  ${typography.small}
  &::first-letter {
    text-transform: capitalize;
  }
`;
const GreyText = styled(Text)`
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
`;
const TitleRow = styled(Row)`
  ${typography.smallBold}
`;
const DotSeparator = styled(FontAwesomeIcon).attrs({
  icon: faCircle
})`
  height: 0.25rem;
  width: 0.25rem;
  ${marginRight.s}
  ${marginY.xs}
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
`;
export function BookingSummaryCard({
  serviceName,
  organisationName,
  resourceName,
  startTime,
  endTime,
  timezone,
  locale
}: BookingSummaryCardProps) {
  const resolvedLocale = locale ?? "en-GB";
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);
  const dateString = startDate.toLocaleDateString(resolvedLocale, {
    ...dateFormats.default,
    timeZone: timezone
  });
  const startTimeString = startDate.toLocaleTimeString(resolvedLocale, {
    ...timeFormats.default,
    timeZone: timezone
  });
  const endTimeString = endDate.toLocaleTimeString(resolvedLocale, {
    ...timeFormats.default,
    timeZone: timezone
  });
  const tzAbbreviation = getTZAbbreviation({
    date: startDate,
    locale: resolvedLocale,
    timezone
  });
  return <Container data-sentry-element="Container" data-sentry-component="BookingSummaryCard" data-sentry-source-file="BookingSummaryCard.tsx">
      <TitleRow data-sentry-element="TitleRow" data-sentry-source-file="BookingSummaryCard.tsx">{serviceName}</TitleRow>
      <Row data-sentry-element="Row" data-sentry-source-file="BookingSummaryCard.tsx">
        {resourceName != null && <>
            <Text data-testid="resource-name">{resourceName}</Text>
            <DotSeparator />
          </>}
        <Text data-sentry-element="Text" data-sentry-source-file="BookingSummaryCard.tsx">{organisationName}</Text>
      </Row>
      <Row data-sentry-element="Row" data-sentry-source-file="BookingSummaryCard.tsx">
        <Text data-sentry-element="Text" data-sentry-source-file="BookingSummaryCard.tsx">{dateString}</Text>
      </Row>
      <Row data-sentry-element="Row" data-sentry-source-file="BookingSummaryCard.tsx">
        <Text data-sentry-element="Text" data-sentry-source-file="BookingSummaryCard.tsx">
          {startTimeString} – {endTimeString}
        </Text>
        {tzAbbreviation && <GreyText>{tzAbbreviation}</GreyText>}
      </Row>
    </Container>;
}
export default BookingSummaryCard;